import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LockOpen } from "lucide-react";
import { useUnlock } from "@/context/TokenDataContext";
import { ECOSYSTEM_PRODUCTS } from "@/config/kingbullish";

const COLORS = ["#FFD700", "#FFB800", "#FFF1A8", "#00FF66", "#8DFFBC", "#00F0FF"];
const DURATION = 3800;

const spawn = (x, y, count, spread, speed) =>
  Array.from({ length: count }, () => {
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * spread;
    const v = speed * (0.55 + Math.random() * 0.75);
    return {
      x, y,
      vx: Math.cos(angle) * v,
      vy: Math.sin(angle) * v,
      size: 4 + Math.random() * 7,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.3,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      round: Math.random() > 0.7,
      life: 1,
    };
  });

const runBurst = (canvas) => {
  const ctx = canvas.getContext("2d");
  const w = (canvas.width = window.innerWidth);
  const h = (canvas.height = window.innerHeight);
  let parts = [
    ...spawn(w / 2, h * 0.55, 160, Math.PI * 1.6, 22),
    ...spawn(0, h * 0.8, 70, Math.PI * 0.7, 26).map((p) => ({ ...p, vx: Math.abs(p.vx) + 6 })),
    ...spawn(w, h * 0.8, 70, Math.PI * 0.7, 26).map((p) => ({ ...p, vx: -Math.abs(p.vx) - 6 })),
  ];
  const start = performance.now();
  let frame;

  const draw = (now) => {
    const t = Math.max(0, (now - start) / DURATION);
    ctx.clearRect(0, 0, w, h);

    const ringR = t * Math.max(w, h) * 0.9;
    ctx.beginPath();
    ctx.arc(w / 2, h * 0.55, ringR, 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(0,255,102,${Math.max(0, 0.6 - t)})`;
    ctx.lineWidth = 3;
    ctx.stroke();

    for (const p of parts) {
      p.vy += 0.42;
      p.vx *= 0.985;
      p.vy *= 0.985;
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.vr;
      p.life = Math.max(0, 1 - t * 1.1);
      ctx.save();
      ctx.globalAlpha = p.life;
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.color;
      ctx.shadowColor = p.color;
      ctx.shadowBlur = 10;
      if (p.round) {
        ctx.beginPath();
        ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
      }
      ctx.restore();
    }
    if (t < 1) frame = requestAnimationFrame(draw);
  };
  frame = requestAnimationFrame(draw);
  return () => cancelAnimationFrame(frame);
};

export const UnlockCelebration = () => {
  const { isUnlocked, marketCap, unlockLabel } = useUnlock();
  const prev = useRef(undefined);
  const [show, setShow] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (marketCap === null) return;
    if (prev.current === false && isUnlocked) setShow(true);
    prev.current = isUnlocked;
  }, [isUnlocked, marketCap]);

  useEffect(() => {
    if (!show) return;
    const stop = runBurst(canvasRef.current);
    const timer = setTimeout(() => setShow(false), DURATION + 600);
    return () => { clearTimeout(timer); stop(); };
  }, [show]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="pointer-events-none fixed inset-0 z-[70]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.6 } }}
          data-testid="unlock-celebration"
        >
          <motion.div
            className="absolute inset-0"
            style={{ background: "radial-gradient(ellipse at 50% 55%, rgba(0,255,102,0.35), rgba(255,215,0,0.12) 35%, transparent 70%)" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0] }}
            transition={{ duration: 1.4, times: [0, 0.15, 1] }}
          />
          <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />
          <div className="absolute inset-0 flex items-center justify-center px-6">
            <motion.div
              className="celebration-card glass hud rounded-2xl px-8 py-7 text-center sm:px-12 sm:py-9"
              style={{ "--hud-color": "#00FF66" }}
              initial={{ scale: 0.6, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 160, damping: 16, delay: 0.1 }}
            >
              <motion.span
                className="mx-auto grid h-16 w-16 place-items-center rounded-xl border border-neon/70 bg-neon/10 text-neon shadow-[0_0_30px_rgba(0,255,102,0.6)]"
                initial={{ rotate: -30, scale: 0.5 }}
                animate={{ rotate: 0, scale: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 12, delay: 0.25 }}
              >
                <LockOpen className="h-8 w-8" />
              </motion.span>
              <div className="mt-5 font-display text-[0.7rem] font-bold tracking-[0.3em] text-gold glow-gold">{unlockLabel} MARKET CAP REACHED</div>
              <div className="mt-2 font-display text-2xl font-black italic tracking-tight text-white sm:text-4xl" data-testid="unlock-celebration-title">
                MAJOR ECOSYSTEM <span className="text-neon glow-green">UNLOCKED</span>
              </div>
              <div className="mt-4 font-body text-sm font-semibold tracking-[0.18em] text-white/70 sm:text-base">
                {ECOSYSTEM_PRODUCTS.map((p) => p.name).join(" · ")}
              </div>
              <div className="mt-1 font-body text-xs font-semibold tracking-[0.3em] text-neon">NOW AVAILABLE</div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
